/**
 * Server functions for the Mundial Prediction Engine.
 *
 * - submitMundialAnswer: authenticated user submits an option for an open event.
 * - settleMundialAnswer: re-checks a resolved event and updates the user's
 *   points_awarded + is_correct fields. Safe to call multiple times.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const SubmitSchema = z.object({
  predictionEventId: z.string().uuid(),
  selectedAnswer: z.string().min(1).max(64),
});

export const submitMundialAnswer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => SubmitSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: event, error: evErr } = await supabase
      .from("prediction_events")
      .select("id,status,end_time,option_a,option_b,option_c")
      .eq("id", data.predictionEventId)
      .maybeSingle();
    if (evErr) throw new Error(evErr.message);
    if (!event) throw new Error("Prediction event not found");
    if (event.status !== "open") throw new Error("Prediction is closed");
    if (new Date(event.end_time).getTime() < Date.now()) {
      throw new Error("Prediction window has expired");
    }
    const valid = [event.option_a, event.option_b, event.option_c].filter(Boolean);
    if (!valid.includes(data.selectedAnswer)) {
      throw new Error("Invalid option");
    }

    const { error: insErr } = await supabase
      .from("prediction_answers")
      .insert({
        user_id: userId,
        prediction_event_id: data.predictionEventId,
        selected_answer: data.selectedAnswer,
        points_awarded: 0,
      });
    if (insErr) throw new Error(insErr.message);

    return { ok: true } as const;
  });

const SettleSchema = z.object({
  predictionEventId: z.string().uuid(),
});

export const settleMundialAnswer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => SettleSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: event, error: evErr } = await supabase
      .from("prediction_events")
      .select("id,status,correct_answer,points_reward")
      .eq("id", data.predictionEventId)
      .maybeSingle();
    if (evErr) throw new Error(evErr.message);
    if (!event || event.status !== "resolved" || !event.correct_answer) {
      return { settled: false, points: 0 } as const;
    }

    const { data: answer } = await supabase
      .from("prediction_answers")
      .select("id,selected_answer,points_awarded,is_correct")
      .eq("user_id", userId)
      .eq("prediction_event_id", data.predictionEventId)
      .maybeSingle();
    if (!answer) return { settled: false, points: 0 } as const;
    if (answer.is_correct !== null) {
      return { settled: true, points: answer.points_awarded } as const;
    }

    const correct = answer.selected_answer === event.correct_answer;
    const points = correct ? event.points_reward : 0;

    const { error: upErr } = await supabase
      .from("prediction_answers")
      .update({ points_awarded: points, is_correct: correct })
      .eq("id", answer.id);
    if (upErr) throw new Error(upErr.message);

    return { settled: true, points, correct } as const;
  });